import {
  BtnClearFormatting,
  BtnStrikeThrough,
  BtnNumberedList,
  EditorProvider,
  BtnBulletList,
  BtnUnderline,
  HtmlButton,
  Separator,
  BtnItalic,
  Toolbar,
  BtnLink,
  BtnRedo,
  BtnBold,
  BtnUndo,
  Editor,
} from 'react-simple-wysiwyg'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { Button, Form, Input, Ref } from 'semantic-ui-react'
import { AxiosError } from 'axios'
import { Helmet } from 'react-helmet'
import * as React from 'react'

import ConfirmRemoveNoteTemplateModal from './ConfirmRemoveNoteTemplateModal'
import useKeyboardShortcut from '../../hooks/useKeyboardShortcut'
import { NoteTemplate } from '../../types'
import { classNames } from '../../utils'
import { useToasts } from '../toasts/ToastsProvider'
import { useApi } from '../../store/mainContext'

type Dialog = { type: 'delete-template' }

type FormValues = { name: string; description: string; body: string }

interface Props {
  onSuccess(): Promise<any>
  template: NoteTemplate
}

export default function NoteTemplateEditor(props: Props) {
  const { template, onSuccess } = props

  const { addToast } = useToasts()
  const api = useApi()

  const form = useForm({
    defaultValues: {
      name: template.name || '',
      description: template.description || '',
      body: template.body || '',
    },
  })

  const [currentDialog, setCurrentDialog] = React.useState<Dialog | null>(null)
  const [isDeleting, setIsDeleting] = React.useState(false)

  const handleDelete = async () => {
    setIsDeleting(true)
    return api
      .delete('note-templates/' + template._id)
      .then(() => onSuccess())
      .then(() => addToast('Template deleted', { variant: 'success' }))
      .catch((e: AxiosError) => {
        addToast(e.response?.data.message || e.message, { variant: 'danger' })
      })
      .finally(() => setIsDeleting(false))
  }

  const [isSaving, setIsSaving] = React.useState(false)

  const onSave: SubmitHandler<FormValues> = async (data) => {
    setIsSaving(true)
    return api
      .patch('note-templates/' + template._id, {
        name: data.name,
        description: data.description || '',
        body: data.body,
      })
      .then(() => onSuccess())
      .then(() => addToast('Template updated', { variant: 'success' }))
      .catch((e: AxiosError) => {
        addToast(e.response?.data.message || e.message, { variant: 'danger' })
      })
      .finally(() => setIsSaving(false))
  }

  const formRef = React.useRef<HTMLFormElement>(null)

  useKeyboardShortcut(() => formRef.current?.requestSubmit(), {
    code: 'KeyS',
    shiftKey: true,
    metaKey: true,
  })

  return (
    <div className="pb-8">
      <Helmet>
        <title>{template.name} - Edit Template</title>
      </Helmet>

      <Ref innerRef={formRef}>
        <Form onSubmit={form.handleSubmit(onSave)} spellCheck="false">
          <div className="sticky top-0 z-10 -mx-2 -mt-5 flex items-start justify-between gap-8 border-b bg-white px-2 pb-3 pt-5">
            <div className="order-1 flex gap-3 pt-1">
              <Button
                onClick={() => setCurrentDialog({ type: 'delete-template' })}
                disabled={isSaving}
                className="!mr-0"
                type="button"
                icon="trash alternate outline"
                color="red"
                basic
              />

              <Button
                className="!mr-0"
                content={
                  <span>
                    Save
                    <span className="pl-4 text-sm font-semibold opacity-70">
                      ⇧⌘S
                    </span>
                  </span>
                }
                type="submit"
                disabled={isSaving}
                loading={isSaving}
                primary
                compact
              />
            </div>
            <div className="max-w-[640px] grow">
              <Controller
                name="name"
                control={form.control}
                rules={{ required: 'Required' }}
                render={({ field, fieldState }) => {
                  return (
                    <Form.Field className="!mb-2.5">
                      <label className="sr-only">Template Name</label>
                      <Input
                        {...field}
                        onChange={(_, { value }) => field.onChange(value)}
                        fluid
                        error={!!fieldState.error}
                        input={
                          <input
                            placeholder="Add a title..."
                            className="!border-none !px-2 !py-1.5"
                            data-1p-ignore
                          />
                        }
                        className="!text-3xl"
                      />
                      <div className="ml-px mt-1 text-base leading-normal text-red-600 empty:hidden">
                        {fieldState.error?.message}
                      </div>
                    </Form.Field>
                  )
                }}
              />
              <Controller
                name="description"
                control={form.control}
                render={({ field, fieldState }) => {
                  return (
                    <Form.Field>
                      <label className="sr-only">Description</label>
                      <Input
                        {...field}
                        onChange={(_, { value }) => field.onChange(value)}
                        fluid
                        error={!!fieldState.error}
                        input={
                          <input
                            placeholder="Add a description..."
                            className="!border-none !px-2 !py-2"
                            data-1p-ignore
                          />
                        }
                      />
                      <div className="ml-px text-base leading-normal text-red-600 empty:hidden">
                        {fieldState.error?.message}
                      </div>
                    </Form.Field>
                  )
                }}
              />
            </div>
          </div>

          <Controller
            name="body"
            control={form.control}
            render={({ field, fieldState }) => {
              const isDisabled = isSaving
              const { ref, ...other } = field
              return (
                <div
                  className={classNames(
                    'relative mt-2',
                    form.formState.errors.body && 'rsw-has-error',
                    isDisabled && 'rsw-is-disabled',
                  )}
                >
                  <label className="sr-only">Body</label>
                  <EditorProvider>
                    <Ref innerRef={ref}>
                      <Editor
                        {...other}
                        containerProps={{ className: 'rsw-editor' }}
                      >
                        <Toolbar>
                          <BtnUndo />
                          <BtnRedo />
                          <Separator />
                          <BtnBold />
                          <BtnItalic />
                          <BtnUnderline />
                          <BtnStrikeThrough />
                          <Separator />
                          <BtnNumberedList />
                          <BtnBulletList />
                          <Separator />
                          <BtnLink />
                          <BtnClearFormatting />
                          <HtmlButton />
                        </Toolbar>
                      </Editor>
                    </Ref>
                  </EditorProvider>
                  <div className="text-base leading-normal text-red-600 empty:hidden">
                    {fieldState.error?.message}
                  </div>
                </div>
              )
            }}
          />
        </Form>
      </Ref>

      <ConfirmRemoveNoteTemplateModal
        open={currentDialog?.type === 'delete-template'}
        isPending={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setCurrentDialog(null)}
      />
    </div>
  )
}
